/**
 * Brand Kit TanStack Query Hooks
 * Handles data fetching, caching, and mutations for brand kit data
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "react-toastify";
import { useTrackEvent } from "../../../libs/analytics";
import { getUserFriendlyErrorMessage } from "../../../api/UserFriendlyError";
import { isDefinitiveClientError } from "../../../api/error-classification";
import {
  getAccountGroupWithBrandKit,
  updateBrandDescription,
  updateBrandAssets,
  createToneProfile,
  updateToneProfile,
  deleteToneProfile,
  updateCustomInstructions,
} from "../api/brand-kits";
import {
  UpdateBrandDescriptionPayload,
  UpdateBrandAssetsPayload,
  CreateToneProfilePayload,
  UpdateToneProfilePayload,
  UpdateCustomInstructionsPayload,
  BrandKit,
} from "../types";
import {
  BRANDKIT_CUSTOM_INSTRUCTION_SAVED,
  BRANDKIT_DESCRIPTION_SAVED,
  BRANDKIT_DESCRIPTION_EDITED,
  BRANDKIT_DESCRIPTION_GENERATION_FAILED,
  BRANDKIT_TOV_SAVED,
} from "../../../constants/Events";
import { extractBrandkitCustomInstructionEventFields } from "../analytics/brandkit-event-payloads";

// Query key factory
export const brandKitKeys = {
  all: ["brand-kit"] as const,
  detail: (groupId: string) => [...brandKitKeys.all, groupId] as const,
};

// Polling configuration
const POLLING_INTERVAL_MS = 2000; // Poll every 2 seconds when processing
const POLLING_TIMEOUT_MS = 3 * 60 * 1000; // Stop polling after 3 minutes

// Retry configuration
/** Maximum number of retry attempts for transient errors (5xx, network). */
const BRAND_KIT_MAX_RETRIES = 3;
/**
 * Exponential backoff capped at 10 s.
 * attemptIndex 0 → 1 s, 1 → 2 s, 2 → 4 s, 3+ → 10 s
 */
const brandKitRetryDelay = (attemptIndex: number): number =>
  Math.min(1000 * 2 ** attemptIndex, 10_000);

/**
 * Check if any brand kit item is currently processing
 */
function isBrandKitProcessing(brandKit?: BrandKit): boolean {
  if (!brandKit) return false;

  const brandDescriptionProcessing =
    brandKit.brandDescription?.currentState === "processing";
  const toneProfileProcessing = brandKit.toneOfVoiceProfiles?.some(
    (p) => p.currentState === "processing"
  );

  return brandDescriptionProcessing || toneProfileProcessing;
}

function isToneOfVoiceGenerationPayload(payload: UpdateToneProfilePayload) {
  return Boolean(
    payload.regenerate ||
    (payload.referenceUrls && payload.referenceUrls.length > 0) ||
    (payload.additionalText && payload.additionalText.trim().length > 0) ||
    (payload.documents && payload.documents.length > 0)
  );
}

/**
 * Hook for fetching brand kit data
 * Polls every 2 seconds while any brand kit item is in "processing" state
 * Stops polling after 3 minutes to prevent infinite polling
 */
export function useBrandKit(groupId: string | null) {
  const query = useQuery({
    queryKey: brandKitKeys.detail(groupId || ""),
    queryFn: async ({ signal }) => {
      if (!groupId) throw new Error("No group ID provided");
      return getAccountGroupWithBrandKit(groupId, signal);
    },
    enabled: !!groupId,
    staleTime: 30_000, // Consider data stale after 30 seconds
    // Retry transient failures (5xx, network errors, token races) with backoff.
    // Never retry definitive client errors (4xx) — they won't recover.
    retry: (failureCount, error) => {
      if (isDefinitiveClientError(error)) return false;
      return failureCount < BRAND_KIT_MAX_RETRIES;
    },
    retryDelay: brandKitRetryDelay,
    // Poll while any brand kit item is processing (handles 504 recovery)
    refetchInterval: (query) => {
      const brandKit = query.state.data?.brandKit;
      const isProcessing = isBrandKitProcessing(brandKit);

      if (!isProcessing) {
        return false;
      }

      // Check if we've been polling too long (3 minute timeout)
      const dataUpdatedAt = query.state.dataUpdatedAt;
      if (dataUpdatedAt && Date.now() - dataUpdatedAt > POLLING_TIMEOUT_MS) {
        // Timeout reached - stop polling
        // The UI will show the last known state (likely "processing")
        // User can manually refresh or retry
        return false;
      }

      return POLLING_INTERVAL_MS;
    },
  });

  // Derived state for convenience
  const brandKit = query.data?.brandKit;

  return {
    ...query,
    brandKit,
  };
}

/**
 * Hook for all brand kit mutations
 * Note: groupId is passed in mutation payloads to avoid stale closure issues
 */
export function useBrandKitMutations() {
  const queryClient = useQueryClient();
  const trackEvent = useTrackEvent();

  // Update brand description mutation
  const updateDescriptionMutation = useMutation({
    mutationFn: async ({
      groupId,
      ...payload
    }: UpdateBrandDescriptionPayload & { groupId: string }) => {
      if (!groupId) throw new Error("No group ID provided");
      return updateBrandDescription(groupId, payload);
    },
    onSuccess: (_, variables) => {
      // Differentiate between manual edit and AI generation
      const isManualEdit =
        "brandDescriptionResult" in variables && !variables.inputs;

      trackEvent({
        eventName: isManualEdit
          ? BRANDKIT_DESCRIPTION_EDITED
          : BRANDKIT_DESCRIPTION_SAVED,
        useServices: ["AMP"],
      });
      queryClient.invalidateQueries({
        queryKey: brandKitKeys.detail(variables.groupId),
      });
      toast.success(
        isManualEdit ? "Brand description saved" : "Brand description generated"
      );
    },
    onError: (error: Error, variables) => {
      // Track generation failures (not manual edit failures)
      const isManualEdit =
        "brandDescriptionResult" in variables && !variables.inputs;

      if (!isManualEdit) {
        trackEvent({
          eventName: BRANDKIT_DESCRIPTION_GENERATION_FAILED,
          metadata: {
            errorMessage: error.message,
          },
          useServices: ["AMP"],
        });
      }

      // Check if this might be a server timeout (5xx) where the backend may still be processing
      // If so, set optimistic "processing" state and let polling handle completion
      if (!isManualEdit && !isDefinitiveClientError(error)) {
        // Server may still be processing (504 Gateway Timeout, etc.)
        // Set optimistic "processing" state to trigger polling
        queryClient.setQueryData(
          brandKitKeys.detail(variables.groupId),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (old: any) => {
            if (!old?.brandKit) return old;
            return {
              ...old,
              brandKit: {
                ...old.brandKit,
                brandDescription: {
                  ...old.brandKit.brandDescription,
                  currentState: "processing",
                  errorMessage: null,
                },
              },
            };
          }
        );
        toast.info(
          "This is taking longer than expected. We'll keep checking in the background."
        );
        return; // Don't show error toast - polling will handle completion
      }

      // Definitive client error (4xx) - invalidate to fetch error state from backend
      queryClient.invalidateQueries({
        queryKey: brandKitKeys.detail(variables.groupId),
      });

      toast.error(
        getUserFriendlyErrorMessage(
          error,
          "Failed to update brand description. Please enter a complete website URL (e.g., https://example.com)."
        )
      );
    },
  });

  // Update brand assets mutation
  const updateAssetsMutation = useMutation({
    mutationFn: async ({
      groupId,
      ...payload
    }: UpdateBrandAssetsPayload & { groupId: string }) => {
      if (!groupId) throw new Error("No group ID provided");
      return updateBrandAssets(groupId, payload);
    },
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({
        queryKey: brandKitKeys.detail(groupId),
      });
      toast.success("Brand assets updated");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update brand assets");
    },
  });

  // Create tone profile mutation with optimistic updates
  const createProfileMutation = useMutation({
    mutationFn: async ({
      groupId,
      ...payload
    }: CreateToneProfilePayload & { groupId: string }) => {
      if (!groupId) throw new Error("No group ID provided");
      return createToneProfile(groupId, payload);
    },
    onMutate: async ({ groupId, ...payload }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: brandKitKeys.detail(groupId),
      });

      // Snapshot previous value
      const previousData = queryClient.getQueryData(
        brandKitKeys.detail(groupId)
      );

      // Create optimistic profile
      const optimisticProfile = {
        _id: `temp-${Date.now()}`,
        name: payload.name,
        description: payload.description || "",
        currentState: "processing" as const,
        inputs: [],
        _pendingPayload: {
          name: payload.name,
          description: payload.description,
          referenceUrls: payload.referenceUrls,
          additionalText: payload.additionalText,
        },
      };

      // Optimistically update cache
      queryClient.setQueryData(
        brandKitKeys.detail(groupId),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (old: any) => {
          if (!old?.brandKit) return old;
          return {
            ...old,
            brandKit: {
              ...old.brandKit,
              toneOfVoiceProfiles: [
                ...old.brandKit.toneOfVoiceProfiles,
                optimisticProfile,
              ],
            },
          };
        }
      );

      return { previousData, optimisticId: optimisticProfile._id, groupId };
    },
    onSuccess: (_, { groupId }) => {
      trackEvent({
        eventName: BRANDKIT_TOV_SAVED,
        useServices: ["AMP"],
      });
      queryClient.invalidateQueries({
        queryKey: brandKitKeys.detail(groupId),
      });
      toast.success("Tone of voice profile created");
    },
    onError: (error: Error, { groupId }, context) => {
      // Check if server might still be processing (5xx timeout)
      // If so, keep optimistic "processing" state and let polling handle completion
      if (!isDefinitiveClientError(error)) {
        // Server may still be processing - keep optimistic profile in "processing" state
        // The polling will pick up the actual result when it's ready
        toast.info(
          "This is taking longer than expected. We'll keep checking in the background."
        );
        return; // Don't update to error state - polling will handle it
      }

      // Definitive client error (4xx) - update the optimistic profile to error state
      if (context?.optimisticId) {
        queryClient.setQueryData(
          brandKitKeys.detail(groupId),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (old: any) => {
            if (!old?.brandKit) return old;

            // Defensive check: verify the optimistic profile still exists
            const profileExists = old.brandKit.toneOfVoiceProfiles.some(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (p: any) => p._id === context.optimisticId
            );

            if (!profileExists) {
              // Profile already removed or updated, invalidate to refresh
              queryClient.invalidateQueries({
                queryKey: brandKitKeys.detail(groupId),
              });
              return old;
            }

            return {
              ...old,
              brandKit: {
                ...old.brandKit,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                toneOfVoiceProfiles: old.brandKit.toneOfVoiceProfiles.map(
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (profile: any) =>
                    profile._id === context.optimisticId
                      ? {
                          ...profile,
                          currentState: "error",
                          errorMessage:
                            error.message ||
                            "Failed to create profile. Please retry.",
                        }
                      : profile
                ),
              },
            };
          }
        );
      }
      toast.error(error.message || "Failed to create tone profile");
    },
  });

  // Update tone profile mutation with optimistic updates for regeneration
  const updateProfileMutation = useMutation({
    mutationFn: async ({
      groupId,
      profileId,
      payload,
    }: {
      groupId: string;
      profileId: string;
      payload: UpdateToneProfilePayload;
    }) => {
      if (!groupId) throw new Error("No group ID provided");
      return updateToneProfile(groupId, profileId, payload);
    },
    onMutate: async ({ groupId, profileId, payload }) => {
      // Only show processing state for regeneration or content source changes
      const willRegenerate = isToneOfVoiceGenerationPayload(payload);

      if (!willRegenerate) {
        return { groupId, profileId, shouldShowProcessing: false };
      }

      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: brandKitKeys.detail(groupId),
      });

      // Snapshot previous value
      const previousData = queryClient.getQueryData(
        brandKitKeys.detail(groupId)
      );

      // Optimistically update the profile to processing state
      queryClient.setQueryData(
        brandKitKeys.detail(groupId),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (old: any) => {
          if (!old?.brandKit) return old;
          return {
            ...old,
            brandKit: {
              ...old.brandKit,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              toneOfVoiceProfiles: old.brandKit.toneOfVoiceProfiles.map(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (profile: any) =>
                  profile._id === profileId
                    ? {
                        ...profile,
                        currentState: "processing",
                        // Update name/description if provided
                        ...(payload.name && { name: payload.name }),
                        ...(payload.description !== undefined && {
                          description: payload.description,
                        }),
                      }
                    : profile
              ),
            },
          };
        }
      );

      return { previousData, groupId, profileId, shouldShowProcessing: true };
    },
    onSuccess: (_, { groupId, payload }) => {
      if (isToneOfVoiceGenerationPayload(payload)) {
        trackEvent({
          eventName: BRANDKIT_TOV_SAVED,
          useServices: ["AMP"],
        });
      }
      queryClient.invalidateQueries({
        queryKey: brandKitKeys.detail(groupId),
      });
      toast.success("Tone of voice profile updated");
    },
    onError: (error: Error, { groupId, profileId }, context) => {
      // Check if server might still be processing (5xx timeout)
      // If so, keep "processing" state and let polling handle completion
      if (context?.shouldShowProcessing && !isDefinitiveClientError(error)) {
        // Server may still be processing - keep "processing" state
        // The polling will pick up the actual result when it's ready
        toast.info(
          "This is taking longer than expected. We'll keep checking in the background."
        );
        return; // Don't update to error state - polling will handle it
      }

      // Definitive client error (4xx) - update to error state if we were showing processing
      if (context?.shouldShowProcessing) {
        queryClient.setQueryData(
          brandKitKeys.detail(groupId),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (old: any) => {
            if (!old?.brandKit) return old;
            return {
              ...old,
              brandKit: {
                ...old.brandKit,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                toneOfVoiceProfiles: old.brandKit.toneOfVoiceProfiles.map(
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (profile: any) =>
                    profile._id === profileId
                      ? {
                          ...profile,
                          currentState: "error",
                          errorMessage:
                            error.message ||
                            "Failed to update profile. Please retry.",
                        }
                      : profile
                ),
              },
            };
          }
        );
      }
      toast.error(error.message || "Failed to update tone profile");
    },
  });

  // Delete tone profile mutation
  const deleteProfileMutation = useMutation({
    mutationFn: async ({
      groupId,
      profileId,
    }: {
      groupId: string;
      profileId: string;
    }) => {
      if (!groupId) throw new Error("No group ID provided");
      return deleteToneProfile(groupId, profileId);
    },
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({
        queryKey: brandKitKeys.detail(groupId),
      });
      toast.success("Tone of voice profile deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete tone profile");
    },
  });

  // Update custom instructions mutation
  const updateCustomInstructionsMutation = useMutation({
    mutationFn: async ({
      groupId,
      ...payload
    }: UpdateCustomInstructionsPayload & { groupId: string }) => {
      if (!groupId) throw new Error("No group ID provided");
      return updateCustomInstructions(groupId, payload);
    },
    onSuccess: (_, variables) => {
      const fields = extractBrandkitCustomInstructionEventFields(variables);
      for (const field of fields) {
        trackEvent({
          eventName: BRANDKIT_CUSTOM_INSTRUCTION_SAVED,
          metadata: { field },
          useServices: ["AMP"],
        });
      }
      queryClient.invalidateQueries({
        queryKey: brandKitKeys.detail(variables.groupId),
      });
      toast.success("Custom instructions saved");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to save custom instructions");
    },
  });

  return {
    updateDescription: updateDescriptionMutation,
    updateAssets: updateAssetsMutation,
    createProfile: createProfileMutation,
    updateProfile: updateProfileMutation,
    deleteProfile: deleteProfileMutation,
    updateCustomInstructions: updateCustomInstructionsMutation,
  };
}
